import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation, Navigate } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { listVariants, filterVariants, byStock } from '../lib/listing';
import { COLLECTIONS, findCollection, matchesCollection } from '../data/collections';
import { DELIVERY, OFFER, BUSINESS } from '../data/pricing';
import { formatINR } from '../lib/quote';
import ProductCard from '../components/ProductCard';
import ListingFilterBar from '../components/ListingFilterBar';
import CredentialRibbon from '../components/CredentialRibbon';
import { track, FUNNEL } from '../lib/track';
import CompareBar from '../components/CompareBar';
import HelpChoosing, { LIVE_GUIDE_SLUGS } from '../components/HelpChoosing';
import { helpForCollection } from '../data/helpChoosing';
import { useTray, toggleTray } from '../lib/compareTray';
import { MAX_COMPARE } from '../data/compare';

// Route: /{slug} for every entry in collections.js — one component, 12 pages.
//
// Each collection page is the /daikin-ac-price-list-delhi shop, pre-filtered
// to its own range: same ribbon, same banner, same short heading, same filter
// bar, same product cards, same bottom links. The only things that differ are
// the heading, the products, and the intro copy — which sits collapsed below
// the heading so the first screen on a phone shows ACs, not paragraphs.
//
// D2C naming, derived from the config h1:
//   "Daikin 1.5 Ton AC Price List" -> H1    "Buy Daikin 1.5 Ton AC Online"
//                                  -> title "Buy Daikin 1.5 Ton AC Online at Best Price | Delhi NCR"
// "Price" stays in the <title> because that is what people type. URLs and
// canonicals are untouched. scripts/collection-body.js writes the same H1 and
// title into the pre-rendered HTML. Keep the two in step.

const shopName = (c) => c.h1.replace(/ Price List$/, '').replace(/ Price$/, '');

const SORTS = {
  price_asc: { label: 'Price: low to high', fn: (a, b) => a.price - b.price },
  price_desc: { label: 'Price: high to low', fn: (a, b) => b.price - a.price },
  discount: { label: 'Biggest saving', fn: (a, b) => (b.mrp - b.price) - (a.mrp - a.price) },
  efficient: { label: 'Most efficient', fn: (a, b) => b.model.iseer - a.model.iseer },
  capacity: { label: 'Capacity', fn: (a, b) => a.model.kw - b.model.kw },
};

const CREDENTIALS = [
  'Authorized Daikin dealer',
  '35+ Years experience',
  'Awarded #1 HVAC Dealer in North India',
  'MD Club Gold Member',
  '50,000+ tonnes installed',
];

// Banner per collection. Any page not listed uses the main Daikin banner.
// To give another page its own banner: drop the .webp files in public/ and
// add one entry here, keyed by the page slug.
const DEFAULT_BANNER = {
  src: '/banner-daikin-desktop.webp',
  srcSet: '/banner-daikin-desktop-900.webp 900w, /banner-daikin-desktop-1400.webp 1400w, /banner-daikin-desktop.webp 2172w',
  width: 2172,
  height: 724,
  alt: 'Buy Daikin ACs at best prices from an authorized dealer in Delhi. 100% authentic products, fast delivery, expert installation.',
};

const BANNERS = {
  'daikin-hot-and-cold-ac-price': {
    src: '/banner-daikin-hot-cold.webp',
    srcSet: '/banner-daikin-hot-cold-900.webp 900w, /banner-daikin-hot-cold.webp 2011w',
    width: 2011,
    height: 782,
    alt: 'Daikin FTHT series hot and cold inverter AC. Heating for cold days, cooling for hot days, energy efficient, advanced filtration, quiet operation.',
  },
};

export default function CollectionPage({ slug: slugProp }) {
  // Static routes mean useParams() is empty, so fall back to the pathname.
  const params = useParams();
  const { pathname } = useLocation();
  const slug = slugProp || params.slug || pathname.replace(/^\/+|\/+$/g, '');
  const collection = findCollection(slug);

  const [ton, setTon] = useState('all');
  const [star, setStar] = useState('all');
  const [kit, setKit] = useState('all');
  const [inverterOnly, setInverterOnly] = useState(false);
  const [sort, setSort] = useState('price_asc');
  const trayIds = useTray();
  const compare = { ids: trayIds, toggle: toggleTray, full: trayIds.length >= MAX_COMPARE };

  // Moving from one collection page to another reuses this component, so
  // filters picked on the 1.5 Ton page must not follow the customer to 1 Ton.
  useEffect(() => {
    setTon('all');
    setStar('all');
    setKit('all');
    setInverterOnly(false);
    setSort('price_asc');
  }, [slug]);

  // Everything in this collection, before the customer's own filters.
  const base = useMemo(() => {
    if (!collection) return [];
    return listVariants()
      .filter((v) => matchesCollection(v.model, collection.filter))
      .sort((a, b) => Number(b.inStock) - Number(a.inStock) || SORTS.price_asc.fn(a, b));
  }, [collection]);

  const list = useMemo(
    () => [...filterVariants(base, { ton, star, kit, inverterOnly })].sort((a, b) => byStock(a, b) || SORTS[sort].fn(a, b)),
    [base, ton, star, kit, inverterOnly, sort],
  );

  // Offer only the filters that can change the result on this page.
  const choices = useMemo(() => ({
    tons: [...new Set(base.map((v) => String(v.model.soldAs)))].sort((a, b) => Number(a) - Number(b)),
    stars: [...new Set(base.map((v) => v.model.stars))].sort((a, b) => a - b),
    showKit: new Set(base.map((v) => v.kit)).size > 1,
    showInverter: new Set(base.map((v) => v.model.inverter)).size > 1,
  }), [base]);

  const models = useMemo(
    () => [...new Map(base.map((v) => [v.model.id, v.model])).values()],
    [base],
  );

  useEffect(() => {
    if (collection) track(FUNNEL.COLLECTION_VIEW, { collection: collection.slug });
  }, [collection]);

  // ItemList schema so Google reads this as a product listing.
  useEffect(() => {
    if (!collection || !models.length) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Buy ${shopName(collection)} Online`,
      numberOfItems: models.length,
      itemListElement: models.map((m, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://www.citicool.in/${m.slug}`,
        name: m.title,
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [collection, models]);

  if (!collection) return <Navigate to="/daikin-ac-price-list-delhi" replace />;

  const name = shopName(collection);
  const banner = BANNERS[collection.slug] || DEFAULT_BANNER;
  const pageTitle = `Buy ${name} Online at Best Price | Delhi NCR`;
  const low = base.length ? (base.find((v) => !v.soldOut) || base[0]).price : null;
  const anyInStock = base.some((v) => v.inStock);
  const others = COLLECTIONS.filter((c) => c.slug !== collection.slug);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={collection.meta} />
      </Helmet>

      <main className="mx-auto max-w-6xl px-4 pt-20 pb-16 lg:pt-24">
        <div className="mb-4 overflow-hidden rounded-xl">
          <CredentialRibbon items={CREDENTIALS} />
        </div>

        {/* Category banner: this page's own if it has one, else the main one. */}
        <picture className="mb-4 block w-full overflow-hidden rounded-xl sm:mb-6">
          <img
            src={banner.src}
            srcSet={banner.srcSet}
            sizes={banner.srcSet ? '(min-width: 1152px) 1120px, 100vw' : undefined}
            alt={banner.alt}
            width={banner.width}
            height={banner.height}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full"
          />
        </picture>

        <nav className="text-[11px] text-gray-500 sm:text-xs">
          <Link to="/daikin-ac-price-list-delhi" className="hover:text-blue-600">All Daikin ACs</Link>
          <span className="mx-1.5">›</span>
          <span className="text-gray-700">{name}</span>
        </nav>

        <h1 className="mt-1 text-xl font-semibold text-gray-900 sm:text-3xl">Buy {name} Online</h1>
        <p className="mt-1 text-xs text-gray-600 sm:mt-2 sm:text-sm">
          {low != null && (
            <>
              {base.length} option{base.length === 1 ? '' : 's'} from{' '}
              <span className="font-medium text-gray-900">{formatINR(low)}</span> · GST included
              {anyInStock && ' · 24-hour delivery on stocked units'}
              {' · '}
            </>
          )}
          Cooling Delhi since {BUSINESS.since} · Authorised Daikin dealer since {BUSINESS.daikinSince}
        </p>

        {OFFER.active && OFFER.note && (
          <div className="mt-3 rounded-lg bg-blue-50 px-4 py-2.5 text-xs text-blue-900 sm:mt-4 sm:text-sm">
            <span className="font-medium">{OFFER.headline}</span> — {OFFER.note}
          </div>
        )}

        {/* Collection copy: collapsed, not deleted — still in the HTML. */}
        <details className="group mt-2">
          <summary className="cursor-pointer list-none text-xs text-blue-700 marker:hidden">
            <span className="inline-flex items-center gap-1">
              <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M8 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              About these models
            </span>
          </summary>
          <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-gray-600 sm:text-sm">
            {collection.intro}
          </p>
          <Link to="/ac-calculator" className="mt-1.5 inline-block text-xs font-medium text-blue-700 hover:underline sm:text-sm">
            Not sure which size? Use the AC size calculator ›
          </Link>
        </details>

        {base.length > 0 && (
          <ListingFilterBar
            ton={ton} setTon={setTon}
            star={star} setStar={setStar}
            kit={kit} setKit={setKit}
            inverterOnly={inverterOnly} setInverterOnly={setInverterOnly}
            sort={sort} setSort={setSort}
            sorts={SORTS}
            count={list.length}
            tonChoices={choices.tons}
            starChoices={choices.stars}
            showKit={choices.showKit}
            showInverter={choices.showInverter}
          />
        )}

        {base.length === 0 ? (
          <p className="mt-10 text-sm text-gray-500">
            Nothing available in this configuration right now.{' '}
            <Link to="/daikin-ac-price-list-delhi" className="text-blue-600 underline">
              See all Daikin ACs
            </Link>.
          </p>
        ) : list.length === 0 ? (
          <p className="mt-12 text-center text-sm text-gray-500">Nothing matches those filters.</p>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((v, i) => <ProductCard key={v.key} variant={v} index={i} compare={compare} />)}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">{DELIVERY.footnote}</p>

        <HelpChoosing links={helpForCollection(collection, LIVE_GUIDE_SLUGS)} className="mt-8 max-w-4xl" />

        <section className="mt-10 border-t border-gray-200 pt-6">
          <h2 className="text-sm font-medium text-gray-900">Browse by size and rating</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to="/daikin-ac-price-list-delhi"
              className="rounded-full border border-blue-600 px-3.5 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-50"
            >
              All Daikin ACs
            </Link>
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                className="rounded-full border border-gray-300 px-3.5 py-1.5 text-xs text-gray-600 transition hover:border-blue-400 hover:text-blue-600"
              >
                {shopName(c)}
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-8 rounded-xl bg-blue-600 px-6 py-6 text-center sm:px-10">
          <p className="text-base font-medium text-white">Buying more than six, or not sure which one?</p>
          <p className="mt-1 text-sm text-blue-100">Bulk pricing is different. Talk to us directly.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${BUSINESS.phone}`}
              onClick={() => track(FUNNEL.PHONE_CLICK, { source: 'collection', collection: collection.slug })}
              className="rounded-md bg-white px-5 py-2.5 text-sm font-medium text-blue-700 transition hover:bg-blue-50"
            >
              Call {BUSINESS.phoneDisplay}
            </a>
            <a
              href={`https://wa.me/${BUSINESS.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'collection', collection: collection.slug })}
              className="rounded-md border-2 border-white px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </main>
      <CompareBar />
    </>
  );
}